import React, { useEffect, useState } from 'react';
import PromptInput from './PromptInput';
import { ResponseInterface } from './PromptResponseList/response-interface';
import PromptResponseList from './PromptResponseList/';
import { chatGPTApi } from '@api/chatgpt';
import { I18n } from 'react-redux-i18n';

const ChatGPT = () => {
  const [responseList, setResponseList] = useState<ResponseInterface[]>([]);
  const [prompt, setPrompt] = useState<string>('');
  const [promptToRetry, setPromptToRetry] = useState<string | null>(null);
  const [uniqueIdToRetry, setUniqueIdToRetry] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [gptVersion, setGptVetsion] = useState<3 | 4>(3);

  let loadInterval: number | undefined;

  const generateUniqueId = () => {
    const timestamp = Date.now();
    const randomNumber = Math.random();
    const hexadecimalString = randomNumber.toString(16);

    return `id-${timestamp}-${hexadecimalString}`;
  };

  const htmlToText = (html: string) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent;
  };

  const delay = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  const addLoader = (uid: string) => {
    const element = document.getElementById(uid) as HTMLElement;
    element.textContent = '';

    // @ts-ignore
    loadInterval = setInterval(() => {
      element.textContent += '.';
      if (element.textContent === '....') {
        element.textContent = '.';
      }
    }, 300);
  };

  const clearLoader = (uid: string) => {
    const element = document.getElementById(uid) as HTMLElement;
    element.textContent = '';
  };

  const addResponse = (isSelf: boolean, response?: string) => {
    const uid = generateUniqueId();
    setResponseList(prevResponses => [
      ...prevResponses,
      {
        id: uid,
        response,
        isSelf,
      },
    ]);
    return uid;
  };

  const updateResponse = (
    uid: string,
    updatedObject: Record<string, unknown>
  ) => {
    clearLoader(uid);
    setResponseList(prevResponses => {
      const updatedList = [...prevResponses];
      const index = prevResponses.findIndex(response => response.id === uid);
      if (index > -1) {
        updatedList[index] = {
          ...updatedList[index],
          ...updatedObject,
        };
      }
      return updatedList;
    });
  };

  const regenerateResponse = async () => {
    await getGPTResult(promptToRetry, uniqueIdToRetry);
  };

  const getGPTResult = async (
    _promptToRetry?: string | null,
    _uniqueIdToRetry?: string | null
  ) => {
    const _prompt = _promptToRetry || htmlToText(prompt);
    if (isLoading || !_prompt) {
      return;
    }

    setIsLoading(true);
    setPrompt('');

    let uniqueId: string;
    if (_uniqueIdToRetry) {
      uniqueId = _uniqueIdToRetry;
    } else {
      addResponse(true, _prompt);
      uniqueId = addResponse(false);
      await delay(50);
      addLoader(uniqueId);
    }

    try {
      const response = await chatGPTApi.getCompletion(prompt, gptVersion);

      updateResponse(uniqueId, {
        response: response.text.trim(),
      });
      setPromptToRetry(null);
      setUniqueIdToRetry(null);
    } catch (err) {
      setPromptToRetry(_prompt);
      setUniqueIdToRetry(uniqueId);
      updateResponse(uniqueId, {
        // @ts-ignore
        response:
          err.status === 401
            ? I18n.t('gui.chatgpt.needAuth')
            : `Error: ${err.message}`,
        error: true,
      });
    } finally {
      clearInterval(loadInterval);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    chatGPTApi.getDialog().then(messages => {
      setResponseList(messages);
    });
  }, []);

  return (
    <div className="container" style={{ padding: '24px 0' }}>
      <p>
        <div style={{ display: 'flex' }}>
          <span style={{ flexGrow: 1 }}>
            {I18n.t('gui.chatgpt.description')}
          </span>

          <select
            value={gptVersion}
            onChange={e => setGptVetsion(e.target.value as any)}
          >
            <option value={3}>GPT-4-mini</option>
            <option value={4}>GPT-4</option>
          </select>
        </div>

        <i>{I18n.t('gui.chatgpt.notice')}</i>
        <br />
        <i>{I18n.t('gui.chatgpt.notice2')}</i>
      </p>
      <div>
        <PromptResponseList responseList={responseList} />
      </div>
      {uniqueIdToRetry && (
        <div>
          <button onClick={() => regenerateResponse()}>
            {isLoading ? 'Loading' : 'Regenerate Response'}
          </button>
        </div>
      )}
      <div style={{ display: 'flex' }}>
        <PromptInput
          prompt={prompt}
          onSubmit={() => getGPTResult()}
          updatePrompt={prompt => setPrompt(prompt)}
        />
        <button
          disabled={isLoading}
          className={isLoading ? 'loading' : ''}
          onClick={() => getGPTResult()}
        >
          ⫸
        </button>
      </div>
    </div>
  );
};

export default ChatGPT;
